import { Button, EmptyState, Notice, Spinner } from '@bennows/boows-react';
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Figure, FigureGroup } from '../../components/Figures';
import { PageHeader } from '../../components/PageHeader';
import { formatDateTime, formatMoney, formatMonth, formatNumber } from '../../lib/format';
import { BILLING_STATES } from '../tenants/useTenants';
import { useOverview, type OperatorOverview } from './useOverview';

/** Start page: tenants, activity, billing states and this month's AI use across all tenants (REQ-140-010). */
export function OverviewPage() {
  const { t } = useTranslation();
  const overview = useOverview();
  const data = overview.data;
  return (
    <div className="ui-stack">
      <PageHeader
        title={t('overview.title')}
        description={data?.metrics_at ? t('overview.asOf', { time: formatDateTime(data.metrics_at) }) : t('overview.description')}
      />
      {overview.isPending ? (
        <div className="ui-console-loading" role="status">
          <Spinner label={t('common.loading')} />
        </div>
      ) : overview.isError ? (
        <div className="ui-error-state" role="alert">
          <p className="ui-error-state__text">{t('overview.loadError')}</p>
          <Button variant="secondary" onClick={() => void overview.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : overview.data.tenants === 0 ? (
        <EmptyState icon={<Building2 aria-hidden="true" />} title={t('overview.emptyTitle')} hint={t('overview.emptyText')} />
      ) : (
        <Figures data={overview.data} />
      )}
    </div>
  );
}

function Figures({ data }: { readonly data: OperatorOverview }) {
  const { t } = useTranslation();
  return (
    <>
      {data.metrics_at ? null : <Notice tone="info">{t('overview.noSnapshot')}</Notice>}
      <FigureGroup title={t('overview.customers')} action={<Link to="/tenants">{t('overview.showTenants')}</Link>}>
        <Figure label={t('overview.figures.tenants')} value={formatNumber(data.tenants)} />
        <Figure label={t('overview.figures.signedUpMonth')} value={formatNumber(data.signed_up_month)} />
        <Figure label={t('overview.figures.active7d')} value={formatNumber(data.active_7d)} />
        <Figure label={t('overview.figures.active30d')} value={formatNumber(data.active_30d)} />
      </FigureGroup>
      <FigureGroup title={t('overview.billing')} note={t('overview.platformNote')}>
        {BILLING_STATES.map((state) => (
          <Figure key={state} label={t(`billingStates.${state}`)} value={formatNumber(data.by_billing_state[state])} />
        ))}
      </FigureGroup>
      <FigureGroup title={t('overview.ai', { month: formatMonth(data.month) })}>
        <Figure label={t('overview.figures.credits')} value={formatNumber(data.credits_month)} />
        <Figure label={t('overview.figures.runs')} value={formatNumber(data.ai_runs_month)} />
        <Figure
          label={t('overview.figures.cost')}
          value={data.ai_cost_month.length ? data.ai_cost_month.map((cost) => formatMoney(cost)).join(' · ') : t('overview.figures.noCost')}
        />
      </FigureGroup>
    </>
  );
}
